/**
 * Condition load — how approved OE Conditions feed the REPS quadrant scores (Beta-1 plan P2-35,
 * Tim C0820 "surveys alone never change; reality vs perception"). Pure module, no Firestore.
 *
 *   perception_q   survey score for quadrant q (0–10, higher = worse) — unchanged from before
 *   load_q         Σ weight(severity) × decay(status, age) over conditions in scope tagged q
 *   load100_q      load_q normalized per 100 people in scope (headcount, else response count)
 *   reality_q      min(10, load100_q × pointsPerUnit)
 *   score_q        mode 'conditions' (default, Tim 20 Oct 2026): reality_q — surveys never move the score
 *                  mode 'blend' (Beta-1 P2-35): min(10, perception_q + min(reality_q, 10 × maxShare))
 *
 * Defaults (DREW settings tab → Scoring): weights I=1 II=2 III=4 IV=8 (same ladder WF6 uses),
 * per 100 people, pointsPerUnit 1, maxShare 0.3 (conditions can add at most 3.0 of the 10),
 * decayDays 30 (a Resolved condition fades linearly to zero over 30 days; Not Approved,
 * Withdrawn and Cancelled never count). A condition tagged with several quadrants counts in
 * full against each of them. Written up in docs/beta/OE-SCORING.md.
 */

export const QUADRANTS = Object.freeze(['reliability', 'efficiency', 'productivity', 'safety']);

export const SCORING_MODES = Object.freeze(['conditions', 'blend']);

export const DEFAULT_SCORING = Object.freeze({
  // Tim, 20 Oct 2026: the REPS scores come from the approved OE Conditions — Drift, Traps,
  // Roadblocks, Landmines — and nothing else. Surveys are perception, reported beside the score
  // for the divergence engine, never added to it. 'blend' (Beta-1 P2-35: perception + capped
  // condition load) stays available per organization for comparison.
  mode: 'conditions',
  conditionWeights: Object.freeze({ I: 1, II: 2, III: 4, IV: 8 }),
  conditionPer: 100, // people
  pointsPerUnit: 1, // score points per weighted condition per `conditionPer` people
  maxShare: 0.3, // conditions can contribute at most maxShare × 10 points to a quadrant
  decayDays: 30, // Resolved conditions fade to zero over this many days
});

/** Statuses that count at full weight. */
export const ACTIVE_CONDITION_STATUSES = Object.freeze(['approved', 'resolution_pending']);

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/** Accept partial / stringy settings (Firestore) and return a complete, sane scoring config. */
export function normalizeScoring(input) {
  const src = input && typeof input === 'object' ? input : {};
  const w = { ...DEFAULT_SCORING.conditionWeights };
  for (const k of Object.keys(w)) {
    const n = Number(src.conditionWeights?.[k]);
    if (Number.isFinite(n) && n >= 0) w[k] = n;
  }
  const num = (v, d, lo = 0, hi = Infinity) => {
    const n = Number(v);
    return Number.isFinite(n) ? clamp(n, lo, hi) : d;
  };
  return {
    mode: SCORING_MODES.includes(src.mode) ? src.mode : DEFAULT_SCORING.mode,
    conditionWeights: w,
    conditionPer: num(src.conditionPer, DEFAULT_SCORING.conditionPer, 1),
    pointsPerUnit: num(src.pointsPerUnit, DEFAULT_SCORING.pointsPerUnit, 0),
    maxShare: num(src.maxShare, DEFAULT_SCORING.maxShare, 0, 1),
    decayDays: num(src.decayDays, DEFAULT_SCORING.decayDays, 0),
  };
}

/** The severity that counts: the highest review level that set one (DH > FLS > ITM). */
export function effectiveSeverity(condition) {
  return condition?.severityDH || condition?.severityFLS || condition?.severityITM || null;
}

function toMillis(v) {
  if (!v) return null;
  if (typeof v === 'number') return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

/** Does a condition fall inside a snapshot scope? ({ type: organization|site|department|team, id }) */
export function conditionInScope(condition, scope) {
  if (!scope || !scope.type || scope.type === 'organization') return true;
  if (scope.type === 'site') return condition.siteId === scope.id;
  if (scope.type === 'department') return condition.deptId === scope.id;
  if (scope.type === 'team') return condition.nwtId === scope.id;
  return true;
}

/** 1 while active, fading to 0 over `decayDays` after Resolved, 0 for everything else. */
export function conditionDecay(condition, scoring = DEFAULT_SCORING, now = Date.now()) {
  const status = condition?.status;
  if (ACTIVE_CONDITION_STATUSES.includes(status)) return 1;
  if (status !== 'resolved') return 0;
  const days = Number(scoring.decayDays);
  if (!(days > 0)) return 0;
  const resolvedAt = toMillis(condition.resolvedAt) ?? toMillis(condition.resolution?.l2ApprovedAt) ?? toMillis(condition.updatedAt);
  if (resolvedAt == null) return 0;
  const age = (now - resolvedAt) / 86400000;
  return clamp(1 - age / days, 0, 1);
}

function quadrantsOf(condition) {
  const list = Array.isArray(condition.repsQuadrants) && condition.repsQuadrants.length
    ? condition.repsQuadrants
    : condition.repsQuadrant
      ? [condition.repsQuadrant]
      : [];
  return list.filter((q) => QUADRANTS.includes(q));
}

/**
 * Compute the condition load for one scope.
 * @returns {{ load, load100, reality, counted, active, resolvedDecaying, people, basis }}
 */
export function computeConditionLoad({ conditions = [], scope = null, headcount = 0, responseCount = 0, scoring, now = Date.now() }) {
  const cfg = normalizeScoring(scoring);
  const load = { reliability: 0, efficiency: 0, productivity: 0, safety: 0 };
  let counted = 0;
  let active = 0;
  let resolvedDecaying = 0;

  for (const c of conditions) {
    if (!c || !conditionInScope(c, scope)) continue;
    const decay = conditionDecay(c, cfg, now);
    if (decay <= 0) continue;
    const weight = cfg.conditionWeights[effectiveSeverity(c)] ?? 0;
    if (weight <= 0) continue;
    const quads = quadrantsOf(c);
    if (quads.length === 0) continue;
    counted += 1;
    if (c.status === 'resolved') resolvedDecaying += 1;
    else active += 1;
    for (const q of quads) load[q] += weight * decay;
  }

  const people = headcount > 0 ? headcount : responseCount > 0 ? responseCount : 0;
  const basis = headcount > 0 ? 'headcount' : responseCount > 0 ? 'responses' : 'none';
  const load100 = {};
  const reality = {};
  // No headcount and no responses: treat the scope as `conditionPer` people so the raw weights
  // become points (a scope with no people data must still score its approved conditions).
  const perUnit = people > 0 ? cfg.conditionPer / people : 1;
  for (const q of QUADRANTS) {
    load100[q] = round2(load[q] * perUnit);
    reality[q] = round2(clamp(load100[q] * cfg.pointsPerUnit, 0, 10));
  }
  return { load, load100, reality, counted, active, resolvedDecaying, people, basis };
}

/**
 * Turn the condition load (and, for reference, the survey perception) into quadrant scores.
 *   mode 'conditions' (default): score = Reality — the approved OE Conditions alone, 0–10.
 *                                Perception is carried through untouched, for display and for
 *                                the divergence engine; it never enters the score. No cap.
 *   mode 'blend' (Beta-1 P2-35): score = Perception + min(Reality, 10 × maxShare), capped at 10.
 * @returns {{ scores, perception, reality, contribution, cap, mode }}
 */
export function blendScores(perception, conditionLoad, scoring) {
  const cfg = normalizeScoring(scoring);
  const conditionsOnly = cfg.mode === 'conditions';
  const cap = conditionsOnly ? 10 : round2(10 * cfg.maxShare);
  const scores = {};
  const contribution = {};
  const p = {};
  const r = {};
  for (const q of QUADRANTS) {
    p[q] = round2(perception?.[q] ?? 0);
    r[q] = round2(conditionLoad?.reality?.[q] ?? 0);
    contribution[q] = round2(Math.min(r[q], cap));
    scores[q] = conditionsOnly ? r[q] : round2(clamp(p[q] + contribution[q], 0, 10));
  }
  return { scores, perception: p, reality: r, contribution, cap, mode: cfg.mode };
}

/** Convenience: perception + conditions → blended quadrant scores in one call. */
export function scoreWithConditions({ perception, conditions, scope, headcount, responseCount, scoring, now }) {
  const load = computeConditionLoad({ conditions, scope, headcount, responseCount, scoring, now });
  const blend = blendScores(perception, load, scoring);
  return { ...blend, conditionLoad: load };
}
